import { useMemo } from "react";
import { StyleSheet, Text, TextInput, View } from "react-native";

import { fontFamily, radius, space, textStyle } from "@passfirst/ui";

import { Button } from "../../components/button";
import { type Colors, useColors } from "../../theme";
import { useDeleteAccount } from "../use-delete-account";
import { Section } from "./section";

// Account-deletion flow. Inline (not Modal) so the layout works on web
// + native without platform-specific Modal quirks. Brief: confirmation
// gate is the literal "DELETE" — both client check + server enforcement.
export function DangerZoneSection(): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const del = useDeleteAccount();

  if (del.state === "idle") {
    return (
      <Section title="Danger zone">
        <Text style={styles.empty}>
          Delete your PassFirst account and wipe all associated data —
          practice history, mastery progress, tutor sessions, purchase
          records. This is final.
        </Text>
        <Button
          fullWidth
          onPress={() => {
            del.setConfirm("");
            del.setError(null);
            del.setState("confirming");
          }}
          accessibilityLabel="Delete account"
          label="Delete account"
          style={styles.destructive}
          testID="settings-delete-account"
        />
      </Section>
    );
  }

  return (
    <Section title="Danger zone">
      <View style={styles.dangerPanel}>
        <Text style={styles.dangerHeading}>This cannot be undone</Text>
        <Text style={styles.empty}>
          Type <Text style={styles.dangerLiteral}>DELETE</Text> below to
          confirm. We&apos;ll wipe your data and email you a confirmation.
        </Text>
        <TextInput
          style={styles.dangerInput}
          value={del.confirm}
          onChangeText={del.setConfirm}
          autoCapitalize="characters"
          autoCorrect={false}
          placeholder="DELETE"
          editable={del.state !== "busy"}
          testID="settings-delete-confirm-input"
        />
        {del.error ? (
          <Text style={styles.error} accessibilityLiveRegion="polite">
            {del.error}
          </Text>
        ) : null}
        <View style={styles.dangerButtonRow}>
          <Button
            variant="secondary"
            onPress={del.reset}
            disabled={del.state === "busy"}
            label="Cancel"
            style={styles.dangerCancel}
          />
          <Button
            onPress={del.confirmDelete}
            disabled={del.state === "busy" || del.confirm !== "DELETE"}
            busy={del.state === "busy"}
            accessibilityLabel="Delete account forever"
            label={del.state === "busy" ? "Deleting…" : "Delete forever"}
            style={[styles.destructive, styles.dangerConfirm]}
            testID="settings-delete-confirm-button"
          />
        </View>
      </View>
    </Section>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    empty: { ...textStyle.bodySmall, color: colors.textMuted, lineHeight: 21 },
    destructive: {
      backgroundColor: colors.error,
      borderColor: colors.error,
    },
    dangerPanel: {
      gap: space[3],
      padding: space[4],
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.error,
      backgroundColor: colors.surfaceWrong,
    },
    dangerHeading: {
      ...textStyle.label,
      color: colors.error,
      fontFamily: fontFamily.bold,
    },
    dangerLiteral: {
      fontFamily: fontFamily.bold,
      color: colors.error,
    },
    dangerInput: {
      ...textStyle.body,
      color: colors.textPrimary,
      backgroundColor: colors.surface,
      borderColor: colors.border,
      borderWidth: 1,
      borderRadius: radius.sm,
      paddingHorizontal: space[3],
      paddingVertical: space[3],
      fontFamily: fontFamily.regular,
    },
    dangerButtonRow: { flexDirection: "row", gap: space[2] },
    dangerCancel: { flex: 1 },
    dangerConfirm: { flex: 1 },
    error: { ...textStyle.bodySmall, color: colors.error },
  });
}
